// Angular
import {
  CommonModule
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnInit,
  Output
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators
} from '@angular/forms';

// Ionic library
import {
  IonIcon,
  IonCheckbox,
  AlertController,
  ModalController,
  IonItem,
  IonLabel,
  IonList,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/angular/standalone';

// Third party
import {
  Subscription,
  Observable,
  takeUntil,
  of
} from 'rxjs';
import { forEach } from 'lodash';

// Local file
import {
  SessionService,
  CommonFacade
} from '@amwager/data-access';
import {
  FundingFacade,
  IAccountsACH,
  IDebitCreditCard
} from '@amwager/funding/data-access';
import {
  IsEmptyPipe,
  CardNamePipe,
  CardDatePipe,
  CardNumEndPipe,
  DateFormatPipe,
  UnsubscribeObservableService
} from '@amwager/util';

import { AddAccountComponent } from './add-account/add-account.component';

@Component({
  selector: 'feature-ion-online-check-deposit',
  templateUrl: './online-check.component.html',
  styleUrls: ['./online-check.component.scss'],
  imports: [
    CommonModule,
    IonIcon,
    IonCheckbox,
    IonList,
    IonItem,
    IonLabel,
    IonGrid,
    IonRow,
    IonCol,
    ReactiveFormsModule,
    IsEmptyPipe,
    CardNamePipe,
    CardDatePipe,
    CardNumEndPipe,
    DateFormatPipe,
    AddAccountComponent
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnlineCheckComponent extends UnsubscribeObservableService implements OnInit {

  private subsFundingLimitResponse!: Subscription;
  private subsGetPendingACHResponse!: Subscription;
  private subsDepositAccountResponse!: Subscription;
  private subsGetAccountResponse!: Subscription;
  private subsAddAccountResponse!: Subscription;
  private subsDeleteAccountResponse!: Subscription;
  private tmsId?: string;
  showDepositForm: boolean = true;
  showAddAccountForm: boolean = false;
  @Output() showAddAccountFormFn = new EventEmitter<boolean>();
  onlineCheckForm!: FormGroup;
  bankAccountId?: string | number;
  accounts$!: Observable<IAccountsACH[]>;
  // accounts$!: any;
  customerFundingLimit: number = 0;
  availableFundingLimit: number = 0;
  pendingList$!: Observable<any[]>;
  amount!: number;
  isAgree: boolean = true;
  isDepositing: boolean = false;

  // convenience getter for easy access to form fields
  get formVal() { return this.onlineCheckForm?.controls }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly sessionService: SessionService,
    private readonly fundingFacade: FundingFacade,
    private readonly elementRef: ElementRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly commonFacade: CommonFacade,
    private readonly modalCtrl: ModalController,
    private readonly alertCtrl: AlertController,
    private readonly ngZone: NgZone
  ) {
    super();

  }

  ngOnInit() {
    this.observerTmsIds();
    this.initForm();

    this.fundingFacade.getAchAccount();
    this.subsGetAccountResponse = this.fundingFacade.getAchAccountList$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response) => {
        if (response !== undefined)
          this.accounts$ = of(response);

        this.cdRef.detectChanges();
        this.cdRef.markForCheck();
      });

    this.fundingFacade.getFundingLimit();
    this.subsFundingLimitResponse = this.fundingFacade.getFundingLimitList$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response) => {
        if (response !== undefined) {
          this.customerFundingLimit = response?.CustomerFundingLimit;
          this.availableFundingLimit = response?.AvailableFundingLimit;
        }

        this.cdRef.markForCheck();
      });

    this.fundingFacade.getPendingAch();
    this.subsGetPendingACHResponse = this.fundingFacade.getPendingAchList$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response) => {
        if (response !== undefined)
          this.pendingList$ = of(response);

        this.cdRef.markForCheck();
      });
  }

  private observerTmsIds(): void {
    this.commonFacade?.tmsId$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((tmsId: string | undefined) => {
        this.tmsId = tmsId;
      });
  }

  chooseAmount(amount: number): void {
    this.formVal['amount']?.setValue(amount);
    this.amount = amount;
  }

  private initForm(): void {
    this.onlineCheckForm = this.formBuilder.group({
      amount: ['', [Validators.required, Validators.minLength(25), Validators.maxLength(1000)]]
    });
  }

  async showAlertMessage(header: string, message: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header,
      message: message,
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
          }
        }
      ]
    });

    await alert.present();
  }

  async openAddAccount(): Promise<void> {
    this.showDepositForm = false;
    this.showAddAccountForm = true;
  }

  async confirmRemoveAccount(bankAccountId: string | number): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Confirmation',
      message: 'Are you sure about removing this bank account?',
      buttons: [
        {
          text: 'Yes',
          handler: () => {
            this.ngZone.run(()=>{
              this.removeAccount(bankAccountId);
            });
          }
        },
        {
          text: 'Cancel',
          role: 'cancel'
        }
      ]
    });

    await alert.present();
  }

  removeAccount(bankAccountId: string | number): void {
    this.fundingFacade.deleteAchAccount(bankAccountId);
    this.subsDeleteAccountResponse = this.fundingFacade.isDeleteAchAccountSuccess$()
    .pipe(takeUntil(this.toUnsubscribe$))
    .subscribe(({response, isSuccess}) => {

      if (isSuccess) {
        this.subsDeleteAccountResponse?.unsubscribe();
        return;
      }

      if (isSuccess !== undefined && isSuccess === false) {
        this.showAlertMessage('Error', response?.error?.Message);
        this.subsDeleteAccountResponse?.unsubscribe();
      }

    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chooseAccount($event: any, method: string, account: string, bankAccountId: string | number): void {

    const accountRows = this.elementRef.nativeElement.querySelectorAll(
      '.online-check-per-row'
    );
    accountRows.forEach((accountRow: HTMLElement) => {
      accountRow.classList.remove('active');
    });

    const accountRow = this.elementRef.nativeElement.querySelector(
      '.online-check-content .online-check-list-container #'
      + account
      + '.online-check-per-row'
    );

    if (accountRow.classList.contains('active')) {
      accountRow.classList.remove('active');
      this.bankAccountId = '';

    }
    else {
      accountRow.classList.add('active');
      this.bankAccountId = bankAccountId;
    }

  }

  depositAmount(): void {
    this.amount = this.formVal['amount']?.value;
  }

  deposit(): void {
    if (this.onlineCheckForm.valid && this.amount && this.bankAccountId && this.tmsId) {
      this.isDepositing = true;

      this.fundingFacade.depositOnlineCheck(
        this.amount,
        this.bankAccountId,
        this.tmsId
      );
      this.subsDepositAccountResponse = this.fundingFacade.isDepositOnlineCheckSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(({response, isSuccess}) => {
        this.isDepositing = false;
        if (!isSuccess && response !== undefined) {
          this.showAlertMessage('Deposit Error', response?.error?.Message);
          this.cdRef.markForCheck();
          this.subsDepositAccountResponse?.unsubscribe();
          return;
        }

        if (response?.Error) {
          this.showAlertMessage('Deposit Error', response?.Error);
          this.cdRef.markForCheck();
          this.subsDepositAccountResponse?.unsubscribe();
          return;
        }

        if (isSuccess) {
          this.showAlertMessage(
          'Successful Deposit',
          'ACH Deposit successful, balance will be updated once ACH has been settled by your bank.'
          );
          this.onlineCheckForm.reset();

          // this.fundingFacade.getPendingAch();

          this.cdRef.markForCheck();
          this.subsDepositAccountResponse?.unsubscribe();
        }
      });
    }
    else {
      this.markFormGroupTouched(this.onlineCheckForm);
    }
  }

  toggleCheckbox(): void {
    this.isAgree = !this.isAgree;
  }

  closeAddAccountForm($event: any): void {
    this.showAddAccountForm = false;
    this.showDepositForm = true;
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    forEach(formGroup.controls, (control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

}
